import type p5 from 'p5';
import { COLOR_PRESETS } from '../utils/ColorUtils';

/**
 * Particle for the ambient animation
 */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  colorIndex: number;
  phase: number;
}

/**
 * Renders ambient animation when no person is detected
 */
export class AmbientRenderer {
  private particles: Particle[] = [];
  private fadeAlpha: number = 1.0;
  private readonly particleCount = 80; // Increased for richer visual

  constructor() {
    this.initializeParticles();
  }

  /**
   * Initialize particle system
   */
  private initializeParticles(): void {
    this.particles = [];
    for (let i = 0; i < this.particleCount; i++) {
      this.particles.push({
        x: Math.random(),
        y: Math.random(),
        vx: (Math.random() - 0.5) * 0.002,
        vy: (Math.random() - 0.5) * 0.002,
        size: Math.random() * 20 + 10,
        colorIndex: Math.floor(Math.random() * COLOR_PRESETS.length),
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  /**
   * Update particle positions
   */
  private updateParticles(): void {
    for (const particle of this.particles) {
      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Wrap around screen edges
      if (particle.x < 0) particle.x = 1;
      if (particle.x > 1) particle.x = 0;
      if (particle.y < 0) particle.y = 1;
      if (particle.y > 1) particle.y = 0;

      // Update phase for wave motion
      particle.phase += 0.02;
    }
  }

  /**
   * Set fade alpha for smooth transition
   * @param alpha - Alpha value (0-1)
   */
  setFadeAlpha(alpha: number): void {
    this.fadeAlpha = Math.max(0, Math.min(1, alpha));
  }

  /**
   * Render the ambient animation
   */
  render(p: p5): void {
    if (this.fadeAlpha <= 0) {
      return;
    }

    this.updateParticles();

    const width = p.width;
    const height = p.height;

    // Draw flowing particles with enhanced glow effect
    p.noStroke();
    for (const particle of this.particles) {
      const color = COLOR_PRESETS[particle.colorIndex];
      if (!color) continue;
      
      const wave = Math.sin(particle.phase) * 0.5 + 0.5;
      const alpha = wave * this.fadeAlpha * 0.7; // Increased opacity

      const screenX = particle.x * width;
      const screenY = particle.y * height;
      const size = particle.size * (0.5 + wave * 0.5);

      // Draw glow effect (outer layer)
      p.fill(color.r, color.g, color.b, alpha * 100);
      p.ellipse(screenX, screenY, size * 1.5, size * 1.5);
      
      // Draw core (inner layer)
      p.fill(color.r, color.g, color.b, alpha * 255);
      p.ellipse(screenX, screenY, size, size);
    }

    // Draw colorful waves with enhanced smoothness
    p.noFill();
    for (let i = 0; i < 5; i++) { // Increased wave count
      const color = COLOR_PRESETS[i % COLOR_PRESETS.length];
      if (!color) continue;
      
      const alpha = this.fadeAlpha * 0.4; // Increased opacity
      p.stroke(color.r, color.g, color.b, alpha * 255);
      p.strokeWeight(3); // Thicker waves

      p.beginShape();
      for (let x = 0; x <= width; x += 15) { // Smoother curve
        const y = height / 2 + 
          Math.sin((x / width) * Math.PI * 3 + p.frameCount * 0.015 + i * 0.5) * 60 +
          Math.sin((x / width) * Math.PI * 2 + p.frameCount * 0.01) * 30;
        p.vertex(x, y);
      }
      p.endShape();
    }

    // Draw text with enhanced styling
    if (this.fadeAlpha > 0.5) {
      // Draw text shadow for depth
      p.fill(0, 0, 0, this.fadeAlpha * 150);
      p.noStroke();
      p.textAlign(p.CENTER, p.CENTER);
      p.textSize(36);
      p.textStyle(p.BOLD);
      p.text('Step into frame to begin', width / 2 + 2, height / 2 + 2);
      
      // Draw main text
      p.fill(255, 255, 255, this.fadeAlpha * 255);
      p.text('Step into frame to begin', width / 2, height / 2);
      
      // Draw subtitle
      p.textSize(18);
      p.textStyle(p.NORMAL);
      p.fill(200, 200, 200, this.fadeAlpha * 200);
      p.text('Move your body to create art', width / 2, height / 2 + 50);
    }
  }

  /**
   * Reset the ambient renderer
   */
  reset(): void {
    this.initializeParticles();
    this.fadeAlpha = 1.0;
  }
}
